/**
 * **Total revenue over time — RV-6, `docs/EDITS-2026-09-24.md` §6.**
 *
 * One series, the estate total, by **day, week or month** (the toggle is in the
 * URL as `?bucket=`). It reads the grid's own data, so it always covers exactly
 * the range and channels the grid below it shows.
 *
 * Two honesty rules, both from how the revenue table is built:
 *
 * · **A blank is not zero.** A day with nothing recorded is a gap in the bars,
 *   never a bar at zero — the seed has a real 27 Jul – 9 Aug recording gap, and
 *   drawing it as fourteen days of no earnings would be a false statement. A
 *   week or month says how many of its days carry a figure.
 * · **A partial bucket says so.** The first and last week or month of a range
 *   are usually cut short by it, and a short bar at the edge reads as a drop.
 */
import { useSearchParams } from "react-router-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { TipCard } from "./ChartTip";
import { Segmented } from "./Tabs";
import { Card } from "./ui";
import { formatDate, formatReadoutDate } from "../lib/dates";
import { formatMoney } from "../lib/money";

export type Bucket = "day" | "week" | "month";

const BUCKETS = [
  { key: "day" as const, label: "Day" },
  { key: "week" as const, label: "Week" },
  { key: "month" as const, label: "Month" },
];

export interface RevenuePoint {
  key: string;
  /** The x-axis tick. */
  label: string;
  /**
   * The hover title: `Sep 02, 2026` / `Week of Sep 01, 2026` / `Sep 2026` — the
   * blueprint's long form, as on Channel Overview's readout (Nilay, 28 Sep). The
   * axis keeps `label`; this is the one place a single bucket is named in full.
   */
  title: string;
  /** `null` when no day in the bucket carries a figure — drawn as a gap. */
  total: number | null;
  daysInBucket: number;
  daysInRange: number;
  daysRecorded: number;
  partial: boolean;
}

function iso(date: Date): string {
  return date.toISOString().slice(0, 10);
}

/** Monday of the ISO week containing `day`. */
function weekStart(day: string): string {
  const date = new Date(`${day}T00:00:00Z`);
  const offset = (date.getUTCDay() + 6) % 7;
  date.setUTCDate(date.getUTCDate() - offset);
  return iso(date);
}

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

function daysInMonth(key: string): number {
  const [year, month] = key.split("-").map(Number) as [number, number];
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/**
 * Buckets per-day totals. `byDay` holds only days with at least one figure;
 * `days` is every day in the range, in order.
 */
export function bucketRevenue(
  days: string[],
  byDay: Map<string, number>,
  bucket: Bucket,
): RevenuePoint[] {
  const groups = new Map<string, string[]>();
  for (const day of days) {
    const key =
      bucket === "day"
        ? day
        : bucket === "week"
          ? weekStart(day)
          : day.slice(0, 7);
    groups.set(key, [...(groups.get(key) ?? []), day]);
  }
  return [...groups.entries()].map(([key, members]) => {
    const recorded = members.filter((day) => byDay.has(day));
    const total = recorded.length
      ? recorded.reduce((sum, day) => sum + (byDay.get(day) ?? 0), 0)
      : null;
    const daysInBucket =
      bucket === "day" ? 1 : bucket === "week" ? 7 : daysInMonth(key);
    const label =
      bucket === "day"
        ? formatDate(key)
        : bucket === "week"
          ? `Week of ${formatDate(key)}`
          : `${MONTHS[Number(key.slice(5, 7)) - 1]} ${key.slice(0, 4)}`;
    const title =
      bucket === "day"
        ? formatReadoutDate(key)
        : bucket === "week"
          ? `Week of ${formatReadoutDate(key)}`
          : label;
    return {
      key,
      label,
      title,
      total,
      daysInBucket,
      daysInRange: members.length,
      daysRecorded: recorded.length,
      partial: members.length < daysInBucket,
    };
  });
}

function tick(value: number): string {
  return value >= 1000
    ? `$${Math.round(value / 100) / 10}k`
    : `$${Math.round(value)}`;
}

export function RevenueChart({
  days,
  byDay,
  currency,
}: {
  days: string[];
  byDay: Map<string, number>;
  currency: string;
}) {
  const [params, setParams] = useSearchParams();
  const raw = params.get("bucket");
  const bucket: Bucket = raw === "week" || raw === "month" ? raw : "day";
  const setBucket = (next: Bucket) =>
    setParams(
      (current) => {
        const updated = new URLSearchParams(current);
        if (next === "day") updated.delete("bucket");
        else updated.set("bucket", next);
        return updated;
      },
      { replace: true },
    );

  const points = bucketRevenue(days, byDay, bucket);

  return (
    <Card
      title={`Total Revenue · ${currency}`}
      info="Every Channel in the Grid Below, Summed. A Gap Means Nothing Was Recorded, Not Zero. Partial Weeks and Months at the Edges of the Range Are Marked."
      action={
        <Segmented
          label="By"
          options={BUCKETS}
          active={bucket}
          onChange={setBucket}
        />
      }
    >
      {byDay.size === 0 ? (
        <p
          className="py-10 text-center text-sm"
          style={{ color: "var(--text-label)" }}
        >
          Nothing Recorded in This Range.
        </p>
      ) : (
        <div style={{ height: "11rem" }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={points}
              margin={{ top: 4, right: 8, bottom: 0, left: 8 }}
              barCategoryGap="12%"
            >
              <CartesianGrid stroke="var(--grid-line)" vertical={false} />
              <XAxis
                dataKey="label"
                tick={{ fill: "var(--axis-label)", fontSize: 11 }}
                stroke="var(--axis-line)"
                interval="preserveStartEnd"
                minTickGap={24}
              />
              <YAxis
                tick={{ fill: "var(--axis-label)", fontSize: 11 }}
                stroke="var(--axis-line)"
                width={56}
                tickFormatter={tick}
              />
              <Tooltip
                cursor={{ fill: "var(--data-1-fill)" }}
                content={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  const point = payload[0]!.payload as RevenuePoint;
                  const rows = [
                    {
                      label: "Total",
                      value:
                        point.total === null
                          ? "Not Recorded"
                          : formatMoney(point.total),
                    },
                  ];
                  if (bucket !== "day") {
                    rows.push({
                      label: "Days Recorded",
                      value: `${point.daysRecorded} of ${point.daysInRange}`,
                    });
                  }
                  return (
                    <TipCard
                      title={point.title}
                      subtitle={
                        point.partial
                          ? `Partial: ${point.daysInRange} of ${point.daysInBucket} Days in This Range`
                          : undefined
                      }
                      rows={rows}
                    />
                  );
                }}
              />
              <Bar
                dataKey="total"
                name="Total Revenue"
                fill="var(--data-1)"
                isAnimationActive={false}
              >
                {/* A partial bucket is drawn lighter, so a short edge bar does not
                  read as a real drop. The tooltip says how partial. */}
                {points.map((point) => (
                  <Cell
                    key={point.key}
                    fillOpacity={point.partial ? 0.45 : 1}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </Card>
  );
}
